import { MoreOutlined } from '@ant-design/icons'
import { OrganizationMember, SupportTicket } from '@zenstackhq/runtime/models'
import {
  Avatar,
  Button,
  Dropdown,
  Flex,
  TableColumnsType,
  Tag,
  Typography,
} from 'antd'
import { OrganizationContactCommon } from '~/features/organizationContact/common'
import { Toolbox } from '~/plugins/toolbox/shared'
import { DataType } from './useData'

const STATUS_COLORS: Record<SupportTicket['status'], string> = {
  OPEN: 'warning',
  CLOSED: 'green-inverse',
}

type Props = {
  organizationMemberUser: OrganizationMember
  onChange: (ticket: Partial<SupportTicket>) => Promise<void>
}

export const useColumns = ({ organizationMemberUser, onChange }: Props) => {
  const getContact = (ticket: DataType) => {
    const participant = ticket.conversation.participants.find(
      participant => !!participant.organizationContact,
    )
    const contact = participant.organizationContact

    return { participant, contact }
  }

  const getParticipantUser = (ticket: DataType) => {
    const participant = ticket.conversation.participants.find(
      candidate => candidate.organizationMemberId === organizationMemberUser.id,
    )

    return { participant }
  }

  const columns: TableColumnsType<DataType> = [
    {
      title: 'Customer',
      dataIndex: 'customer',
      key: 'customer',
      width: 300,
      render: (_, ticket) => {
        const { participant, contact } = getContact(ticket)

        const names = OrganizationContactCommon.Helper.getNames(contact)

        const color = OrganizationContactCommon.Helper.getColor(contact)
        const initials = Toolbox.Utility.stringToInitials(names.full)

        return (
          <>
            <Flex align="center" gap={8}>
              <Flex>
                <Avatar
                  size={44}
                  src={contact.pictureUrl}
                  style={{
                    background: color,
                  }}
                >
                  {initials}
                </Avatar>
              </Flex>

              <Flex vertical style={{ overflow: 'hidden' }}>
                <Typography.Text strong ellipsis>
                  {names.name}
                </Typography.Text>
                <Typography.Text ellipsis>{contact.email}</Typography.Text>
              </Flex>
            </Flex>
          </>
        )
      },
    },
    {
      title: '',
      dataIndex: 'message',
      key: 'message',
      ellipsis: true,
      render: (_, ticket) => {
        const content = ticket.conversation.messages[0]?.content
        const { participant } = getParticipantUser(ticket)

        const isNotRead = participant?.countUnreadMessages > 0
        return (
          <Flex align="center">
            <Flex flex={1} style={{ overflow: 'hidden' }}>
              <Typography.Text type="secondary" ellipsis>
                {content}
              </Typography.Text>
            </Flex>
            {isNotRead && (
              <Flex vertical>
                <Tag
                  color="red-inverse"
                  style={{
                    margin: 0,
                    borderRadius: '10px',
                    fontSize: '11px',
                    lineHeight: '15px',
                  }}
                >
                  {participant.countUnreadMessages}
                </Tag>
              </Flex>
            )}
          </Flex>
        )
      },
    },
    {
      title: 'Status',
      key: 'status',
      width: 130,
      render: (_, ticket) => {
        const status = ticket.status

        const color = STATUS_COLORS[status]

        return <Tag color={color}>{status}</Tag>
      },
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 130,
      render: (_, ticket) => {
        const date = ticket.createdAt

        return (
          <Flex vertical>
            <Typography.Text>
              {Toolbox.Date.toNotionStyle(date)}
            </Typography.Text>
          </Flex>
        )
      },
    },
    {
      key: 'actions',
      width: 50,
      render: (_, ticket) => {
        const isClosed = ticket.status === 'CLOSED'

        return (
          <Flex gap={16} onClick={event => event.stopPropagation()}>
            <Dropdown
              menu={{
                items: [
                  isClosed && {
                    key: 'OPEN',
                    label: 'Re-open',
                    onClick: () => onChange({ id: ticket.id, status: 'OPEN' }),
                  },
                  !isClosed && {
                    key: 'CLOSED',
                    label: 'Close',
                    onClick: () =>
                      onChange({ id: ticket.id, status: 'CLOSED' }),
                  },
                ].filter(Boolean),
              }}
              trigger={['click']}
            >
              <Button icon={<MoreOutlined />} size="small" type="text"></Button>
            </Dropdown>
          </Flex>
        )
      },
    },
  ]

  return {
    columns,
  }
}
